import type { Locale } from 'lc-services/types'
import {
  buildRepositoriesV2,
  GeoCoding,
  HouseRepository,
  MultiInquiryWishlistsRepository,
  RequestRepositoryMerged,
  RequestWishlistsRepository,
  UserLocation,
  UserRepositoryMerged,
} from 'lc-repository'
import { searchBuild } from 'lc-services/modules/search/searchBuild'
import { wishlistBuild } from 'lc-services/modules/wishlist'
import { userPropertiesBuild } from 'lc-services/modules/user'

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const { getCookie, setCookie } = nuxtApp.$cookieStore as any
  const $i18n = nuxtApp.$i18n as {
    locale: Ref<Locale>
  }
  const locale: Ref<Locale> = $i18n.locale
  const API_BASE_URL = config.public.apiURL as string

  const lcRepositories = buildRepositoriesV2.lc({
    api: {
      name: 'lc',
      url: API_BASE_URL,
      source: 'website',
    },
    locale: locale.value,
    httpClientType: 'ofetch',
    storageClient: {
      getItem: (key: string) => getCookie(key),
      setItem: (key: string, value: string) => setCookie(key, value),
    },
  })

  const userLocation = new UserLocation({
    apiUrl: 'https://api.ipregistry.co',
    apiKey: config.public.ipRegistryApiKey,
    httpClientType: 'axios',
  })

  watch(
    () => locale.value,
    (newValue) => {
      lcRepositories.localeCustom.setLocale(newValue)
    },
  )

  const { controller: searchController, presenter: searchPresenter } =
    searchBuild({
      repository: {
        houses: HouseRepository(lcRepositories.httpClient),
        request: RequestRepositoryMerged(lcRepositories.httpClient),
        geocoding: config.public.featureFlags.unknownDestinations
          ? undefined
          : new GeoCoding({
              apiUrl: 'https://api.mapbox.com',
              httpClientType: 'ofetch',
              apiKey: config.public.mapboxApiKey,
            }),
      },
    })

  const { controller: wishlistController, presenter: wishlistPresenter } =
    wishlistBuild(
      UserRepositoryMerged(lcRepositories.httpClient),
      MultiInquiryWishlistsRepository(lcRepositories.httpClient),
      RequestWishlistsRepository(lcRepositories.httpClient),
    )

  const {
    controller: userPropertiesController,
    presenter: userPropertiesPresenter,
  } = userPropertiesBuild(userLocation)

  return {
    provide: {
      lcRepositories,
      lcServicesSearch: {
        searchController,
        searchPresenter,
      },
      lcServicesWishlist: {
        wishlistController,
        wishlistPresenter,
      },
      lcServicesUserProperties: {
        userPropertiesController,
        userPropertiesPresenter,
      },
    },
  }
})
